import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { concatMap, map, reduce, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';

export interface StaffUserPermission {
  id: number;
  permissions_name?: string | null;
  slug: string;
  description?: string | null;
  model?: string | null;
  is_active?: boolean;
  is_deleted?: boolean;
}

export interface StaffUserRolePermissionLink {
  permission_id: number;
  permission?: StaffUserPermission | null;
}

export interface StaffUserRoleDetail {
  id: number;
  role_name: string;
  slug: string;
  role_permissions?: StaffUserRolePermissionLink[];
}

export interface StaffUserRow {
  id: number;
  uuid?: string;
  unique_id?: string;
  full_name: string;
  email_id: string;
  mobile_number: string;
  country_code: string;
  status: string;
  profile_picture?: unknown;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  user_roles?: Array<{
    role_id: number;
    role?: StaffUserRoleDetail;
  }>;
}

export interface StaffUserListMeta {
  total: number;
  lastPage: number;
  currentPage: number;
  perPage: number;
  prev: number | null;
  next: number | null;
}

export interface StaffUserListPayload {
  result: StaffUserRow[];
  meta: StaffUserListMeta;
}

export interface StaffUserListQuery {
  page?: number;
  page_size?: number;
  searchText?: string;
  status?: 'Active' | 'InActive';
  created_at_from?: string;
  created_at_to?: string;
  deleted_at_from?: string;
  deleted_at_to?: string;
  sortColumn?: string;
  sortBy?: 'asc' | 'desc' | 'ASC' | 'DESC';
}

@Injectable({ providedIn: 'root' })
export class StaffUserApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  listUsers(query: StaffUserListQuery = {}): Observable<ApiEnvelope<StaffUserListPayload>> {
    return this.http.get<ApiEnvelope<StaffUserListPayload>>(`${this.base}/user/list`, {
      params: this.buildParams(query, 'active'),
    });
  }

  listDeletedUsers(query: StaffUserListQuery = {}): Observable<ApiEnvelope<StaffUserListPayload>> {
    return this.http.get<ApiEnvelope<StaffUserListPayload>>(`${this.base}/user/deleted`, {
      params: this.buildParams(query, 'deleted'),
    });
  }

  /**
   * Fetches every page for the given filter (for Excel export).
   * Pass a large `page_size` in `filter` (e.g. 500) to minimize round-trips.
   */
  fetchAllStaffUserRows(
    filter: Omit<StaffUserListQuery, 'page'>,
    deleted: boolean,
  ): Observable<StaffUserRow[]> {
    const listFn = deleted
      ? (q: StaffUserListQuery) => this.listDeletedUsers(q)
      : (q: StaffUserListQuery) => this.listUsers(q);

    return listFn({ ...filter, page: 1 }).pipe(
      switchMap((first) => {
        const envelope = first?.data;
        if (!envelope?.meta) {
          return of([]);
        }
        const rows = envelope.result ?? [];
        const lastPage = envelope.meta.lastPage;
        if (lastPage <= 1) {
          return of(rows);
        }
        const restPageNums = Array.from({ length: lastPage - 1 }, (_, i) => i + 2);
        return from(restPageNums).pipe(
          concatMap((page) =>
            listFn({ ...filter, page }).pipe(
              map((r) => r.data?.result ?? []),
            ),
          ),
          reduce((acc, chunk) => acc.concat(chunk), rows),
        );
      }),
    );
  }

  getUserById(id: number): Observable<ApiEnvelope<StaffUserRow>> {
    return this.http.get<ApiEnvelope<StaffUserRow>>(`${this.base}/user/${id}`);
  }

  getUserByUuid(uuid: string): Observable<ApiEnvelope<StaffUserRow>> {
    const u = encodeURIComponent(uuid.trim());
    return this.http.get<ApiEnvelope<StaffUserRow>>(`${this.base}/user/by-uuid/${u}`);
  }

  createUser(formData: FormData): Observable<ApiEnvelope<StaffUserRow>> {
    return this.http.post<ApiEnvelope<StaffUserRow>>(`${this.base}/user`, formData);
  }

  updateUser(id: number, formData: FormData): Observable<ApiEnvelope<StaffUserRow>> {
    return this.http.patch<ApiEnvelope<StaffUserRow>>(`${this.base}/user/${id}`, formData);
  }

  deleteUser(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(`${this.base}/user/${id}`);
  }

  permanentDeleteUser(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(
      `${this.base}/user/permanent-delete/${id}`,
    );
  }

  restoreUser(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.patch<ApiEnvelope<unknown>>(`${this.base}/user/${id}/restore`, {});
  }

  toggleUserStatus(id: number): Observable<ApiEnvelope<StaffUserRow>> {
    return this.http.patch<ApiEnvelope<StaffUserRow>>(`${this.base}/user/${id}/toggle-status`, {});
  }

  deleteProfilePicture(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(`${this.base}/user/profile-picture/${id}`);
  }

  /** Resend invitation / password-setup email to an active user (`POST /user/:id/resend-password-link`). */
  resendUserPasswordLink(userId: number): Observable<ApiEnvelope<unknown>> {
    return this.http.post<ApiEnvelope<unknown>>(
      `${this.base}/user/${userId}/resend-password-link`,
      {},
    );
  }

  private buildParams(
    query: StaffUserListQuery,
    mode: 'active' | 'deleted',
  ): HttpParams {
    let params = this.buildCommonListParams(query);
    return mode === 'active'
      ? this.applyActiveListParams(params, query)
      : this.applyDeletedListParams(params, query);
  }

  private buildCommonListParams(query: StaffUserListQuery): HttpParams {
    let params = new HttpParams();
    if (query.page != null) params = params.set('page', query.page);
    if (query.page_size != null) params = params.set('page_size', query.page_size);
    if (query.searchText) params = params.set('searchText', query.searchText);
    if (query.sortColumn) params = params.set('sortColumn', query.sortColumn);
    if (query.sortBy) {
      const dir = String(query.sortBy).toLowerCase() === 'asc' ? 'asc' : 'desc';
      params = params.set('sortBy', dir);
    }
    return params;
  }

  private applyActiveListParams(params: HttpParams, query: StaffUserListQuery): HttpParams {
    if (query.status) params = params.set('status', query.status);
    if (query.created_at_from) params = params.set('created_at_from', query.created_at_from);
    if (query.created_at_to) params = params.set('created_at_to', query.created_at_to);
    return params;
  }

  private applyDeletedListParams(params: HttpParams, query: StaffUserListQuery): HttpParams {
    if (query.deleted_at_from) params = params.set('deleted_at_from', query.deleted_at_from);
    if (query.deleted_at_to) params = params.set('deleted_at_to', query.deleted_at_to);
    return params;
  }
}
